<template>
  <!-- Modal Backdrop -->
  <div v-if="isOpen" class="modal-backdrop" @click="handleCancel">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">Import Risks</h2>
        <button class="modal-close" @click="handleCancel" aria-label="Close import wizard">&times;</button>
      </div>

      <!-- Wizard steps -->
      <div class="wizard-steps">
        <div
          v-for="(step, index) in wizardStepLabels"
          :key="index"
          class="wizard-step"
          :class="{
            'wizard-step--current': importState.currentStep === index + 1,
            'wizard-step--completed': importState.currentStep > index + 1
          }"
        >
          <div class="wizard-step__number">{{ index + 1 }}</div>
          <span class="wizard-step__label">{{ step }}</span>
          <div v-if="index < wizardStepLabels.length - 1" class="wizard-step__connector"></div>
        </div>
      </div>

      <!-- Step 1: Paste Data (v-show so spreadsheet stays mounted when navigating back) -->
      <div v-show="importState.currentStep === 1" class="modal-content">
        <p class="step-description">Paste your risk data from Excel or Google Sheets below:</p>
        <div v-if="importState.errorMessage" class="error-message">{{ importState.errorMessage }}</div>
        <div ref="spreadsheetContainer" class="spreadsheet-container"></div>
      </div>

      <!-- Step 2: Header Reconciliation -->
      <div v-show="importState.currentStep === 2" class="modal-content">
        <p class="step-description">Map your columns to the expected headers:</p>
        <div v-if="importState.errorMessage" class="error-message">{{ importState.errorMessage }}</div>
        <div class="header-mapping">
          <div v-for="expectedHeader in expectedHeaders" :key="expectedHeader" class="mapping-row">
            <label class="mapping-label">{{ expectedHeader }}:</label>
            <select v-model="importState.headerMapping[expectedHeader]" class="mapping-select">
              <option value="">-- Select Column --</option>
              <option v-for="userHeader in importState.userHeaders" :key="userHeader" :value="userHeader">
                {{ userHeader }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Step 3: Confirmation -->
      <div v-show="importState.currentStep === 3" class="modal-content">
        <div class="confirmation-message">
          <div class="success-icon">✓</div>
          <p class="confirmation-text">Your data is ready to be imported!</p>
          <p class="confirmation-subtext">{{ importState.risksToImport.length }} risk(s) will be added to the risk map.</p>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="importState.isLoading" class="loading-overlay">
        <div class="loader"></div>
        <p class="loading-text">Importing risks...</p>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <button @click="handleCancel" class="btn btn-secondary">Cancel</button>
        <button v-if="importState.currentStep === 2 || importState.currentStep === 3" @click="handleBack" class="btn btn-secondary">
          Back
        </button>
        <button v-if="importState.currentStep === 1" @click="handleNext" class="btn btn-primary">Next</button>
        <button v-if="importState.currentStep === 2" @click="handleNext" class="btn btn-primary">Next</button>
        <button v-if="importState.currentStep === 3" @click="handleFinish" class="btn btn-success" :disabled="importState.isLoading">
          Finish
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { Risk } from '../../../riskMap/types/Risk';
import { EXPECTED_IMPORT_HEADERS, processImportHeaders, parseImportData } from '../stores/RiskMapStore';

// Import jspreadsheet and jsuites
let jspreadsheet: any = null;
let jsuites: any = null;
let librariesLoaded = false;

// Dynamically import jspreadsheet
const loadLibraries = async () => {
  if (typeof window !== 'undefined' && !librariesLoaded) {
    const [jspreadsheetModule, jsuitesModule] = await Promise.all([
      import('jspreadsheet-ce'),
      import('jsuites')
    ]);
    jspreadsheet = jspreadsheetModule.default || jspreadsheetModule;
    jsuites = jsuitesModule.default || jsuitesModule;
    librariesLoaded = true;
  }
};

interface Props {
  isOpen: boolean;
  onImport?: (risks: Omit<Risk, 'id'>[]) => Promise<void>;
}

const CLOSE_EVENT = 'close';
const IMPORT_RISKS_EVENT = 'import-risks';

const props = defineProps<Props>();
const emit = defineEmits([CLOSE_EVENT, IMPORT_RISKS_EVENT]);

const spreadsheetContainer = ref<HTMLElement | null>(null);
const spreadsheetInstance = ref<any>(null);
const importState = ref<{
  isOpen: boolean;
  isLoading: boolean;
  errorMessage: string;
  risksToImport: Omit<Risk, 'id'>[];
  currentStep: number;
  userHeaders: string[];
  headerMapping: Record<string, string>;
}>({
  isOpen: props.isOpen,
  isLoading: false,
  errorMessage: '',
  risksToImport: [],
  currentStep: 1,
  userHeaders: [],
  headerMapping: {}
});

const expectedHeaders = EXPECTED_IMPORT_HEADERS;
const wizardStepLabels = ['Paste Data', 'Map Headers', 'Confirm'];
const columnTitles = {
  description: 'Description',
  likelihood: 'Likelihood',
  severity: 'Severity',
  residualLikelihood: 'Residual Likelihood',
  residualSeverity: 'Residual Severity'
};

// Initialize spreadsheet when modal opens
watch(() => props.isOpen, async (newValue) => {
  if (newValue) {
    await loadLibraries();
    await nextTick();
    initializeSpreadsheet();
  }
});

const initializeSpreadsheet = () => {
  if (!spreadsheetContainer.value || !jspreadsheet) return;

  // Clear existing spreadsheet if any (v5 returns array of worksheets; no per-instance destroy)
  if (spreadsheetInstance.value && spreadsheetContainer.value) {
    spreadsheetContainer.value.innerHTML = '';
    spreadsheetInstance.value = null;
  }

  // Initialize with empty data and default headers
  const columns = [
    { type: 'text', title: columnTitles.description, width: 300 },
    { type: 'numeric', title: columnTitles.likelihood, width: 100 },
    { type: 'numeric', title: columnTitles.severity, width: 100 },
    { type: 'numeric', title: columnTitles.residualLikelihood, width: 150 },
    { type: 'numeric', title: columnTitles.residualSeverity, width: 150 }
  ];

  spreadsheetInstance.value = jspreadsheet(spreadsheetContainer.value, {
    worksheets: [{
      data: [[]],
      columns: columns,
      minDimensions: [5, 10],
      allowInsertRow: true,
      allowInsertColumn: false,
      allowDeleteRow: true,
      allowDeleteColumn: false,
      tableOverflow: true,
      tableHeight: '400px'
    }]
  });
};


const handleNext = () => {
  if (importState.value.currentStep === 1) {
    processSpreadsheetData();
  } else if (importState.value.currentStep === 2) {
    mapDataToRisks();
  }
};

const processSpreadsheetData = () => {
  const worksheet = spreadsheetInstance.value?.[0];
  if (!worksheet) return;

  const data = worksheet.getData();
  if (!data || data.length === 0) {
    importState.value.errorMessage = 'Please paste some data first';
    return;
  }

  // Clear any previous error
  importState.value.errorMessage = '';

  const result = processImportHeaders(data);
  importState.value.userHeaders = result.userHeaders;
  importState.value.headerMapping = result.headerMapping;

  if (result.autoMapped) {
    // Headers match - skip reconciliation step and go directly to confirmation
    mapDataToRisks();
    importState.value.currentStep = 3;
  } else {
    // Headers don't match - go to reconciliation step
    importState.value.currentStep = 2;
  }
};

const mapDataToRisks = () => {
  const worksheet = spreadsheetInstance.value?.[0];
  if (!worksheet) return;

  const data = worksheet.getData();
  const result = parseImportData(data, importState.value.headerMapping);

  if (result.risks.length === 0) {
    importState.value.errorMessage = result.errorMessage || 'No valid risks found in the data';
    return;
  }

  // Show warning if some rows were skipped
  if (result.errorMessage) {
    importState.value.errorMessage = result.errorMessage;
  }

  importState.value.risksToImport = result.risks;
  importState.value.currentStep = 3;
};

const handleFinish = async () => {
  importState.value.isLoading = true;
  importState.value.errorMessage = '';
  try {
    if (props.onImport) {
      await props.onImport(importState.value.risksToImport);
    } else {
      emit(IMPORT_RISKS_EVENT, importState.value.risksToImport);
    }
    handleClose();
  } catch (error) {
    console.error('Error importing risks:', error);
    importState.value.errorMessage = error instanceof Error ? error.message : 'Failed to import risks. Please try again.';
  } finally {
    importState.value.isLoading = false;
  }
};

const handleCancel = () => {
  handleClose();
};

const handleBack = () => {
  if (importState.value.currentStep > 1) {
    importState.value.errorMessage = '';
    importState.value.currentStep--;
  }
};

const handleClose = () => {
  // Reset state
  importState.value.currentStep = 1;
  importState.value.userHeaders = [];
  importState.value.headerMapping = {};
  importState.value.risksToImport = [];
  importState.value.isLoading = false;
  importState.value.errorMessage = '';
  
  // Remove spreadsheet from DOM (v5 returns array of worksheets; no per-instance destroy)
  if (spreadsheetInstance.value && spreadsheetContainer.value) {
    spreadsheetContainer.value.innerHTML = '';
    spreadsheetInstance.value = null;
  }
  
  emit(CLOSE_EVENT);
};
</script>

<style scoped>
/* Load jspreadsheet styles */
@import 'jspreadsheet-ce/dist/jspreadsheet.css';
@import 'jsuites/dist/jsuites.css';

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-container {
  background-color: #2a2a2a;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  max-width: 900px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #444;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #f1f5f9;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 28px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
}

.wizard-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 16px 24px;
  border-bottom: 1px solid #444;
  background-color: rgba(0, 0, 0, 0.15);
}

.wizard-step {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

.wizard-step__number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #444;
  color: #9ca3af;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wizard-step--current .wizard-step__number {
  background-color: #3b82f6;
  color: white;
}

.wizard-step--completed .wizard-step__number {
  background-color: #4caf50;
  color: white;
}

.wizard-step__label {
  margin-left: 8px;
  font-size: 14px;
  color: #9ca3af;
  white-space: nowrap;
}

.wizard-step--current .wizard-step__label {
  color: #f1f5f9;
  font-weight: 500;
}

.wizard-step--completed .wizard-step__label {
  color: #94a3b8;
}

.wizard-step__connector {
  width: 40px;
  height: 2px;
  background-color: #444;
  margin: 0 12px 0 16px;
}

.wizard-step--completed .wizard-step__connector {
  background-color: #4caf50;
}

.modal-content {
  padding: 24px;
  flex: 1;
  overflow-y: auto;
  min-height: 400px;
}

.step-description {
  margin-bottom: 16px;
  color: #cbd5e1;
  font-size: 14px;
}

.error-message {
  margin-bottom: 16px;
  padding: 12px 16px;
  background-color: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.3);
  border-radius: 6px;
  color: #fca5a5;
  font-size: 14px;
  font-weight: 500;
  white-space: pre-line;
  max-height: 150px;
  overflow-y: auto;
}

.spreadsheet-container {
  width: 100%;
  background-color: #fff;
  border-radius: 4px;
  overflow: auto;
  color: #000;
}

/* Force black text in jspreadsheet (overrides dark modal inheritance) */
.spreadsheet-container :deep(.jss_container),
.spreadsheet-container :deep(.jss_worksheet),
.spreadsheet-container :deep(.jss_worksheet td),
.spreadsheet-container :deep(.jss_worksheet input),
.spreadsheet-container :deep(.jss_toolbar),
.spreadsheet-container :deep(.jss_toolbar *) {
  color: #000;
}

.header-mapping {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mapping-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mapping-label {
  flex: 0 0 180px;
  color: #f1f5f9;
  font-weight: 500;
  font-size: 14px;
}

.mapping-select {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #555;
  border-radius: 4px;
  background-color: #333;
  color: #f1f5f9;
  font-size: 14px;
  cursor: pointer;
}

.mapping-select:focus {
  outline: none;
  border-color: #4caf50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.mapping-select option {
  background-color: #333;
  color: #f1f5f9;
}

.confirmation-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.success-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #4caf50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  font-weight: bold;
  margin-bottom: 24px;
}

.confirmation-text {
  font-size: 20px;
  font-weight: 600;
  color: #f1f5f9;
  margin-bottom: 12px;
}

.confirmation-subtext {
  font-size: 14px;
  color: #cbd5e1;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(42, 42, 42, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.loader {
  border: 4px solid #555;
  border-top: 4px solid #4caf50;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 16px;
  color: #f1f5f9;
  font-size: 16px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #444;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #555;
  color: #f1f5f9;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #666;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
}

.btn-success {
  background-color: #4caf50;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #45a049;
}
</style>
